import {createSelector} from 'reselect'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import {isPresent} from '../../../common/utils.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import TextInput from '../../../common/components/TextInput.js'
import Select from '../../../common/components/Select.js'
import {
  updateLabelConfig,
  labelConfigSelector,
  SENDER_TAX_INFO,
  taxInfoProperties,
  taxIDNumberTemplate,
  taxIDTypeTemplate,
  taxIssuerCountryTemplate,
  TAX_INFO_NEEDS_COUNTRY_SHIPPERS,
} from '../../../data/labelInfos/index.js'
import {getRates} from '../../../data/labelInfos/rateRequest.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import ShipperOptions from '../../../data/shipperOptions.js'

const MODAL_FORM = 'tax_info_modal'

export function showTaxInfoModal(labelInfoID, shipperType) {
  const labelConfig = labelConfigSelector(getState(), {
    labelInfoID,
  })
  const properties = taxInfoProperties(shipperType)

  setForm(MODAL_FORM, {
    labelInfoID,
    shipperType,
    properties,
    ...properties.reduce(
      (prev, property) => ({
        ...prev,
        [taxIDNumberTemplate(property)]:
          labelConfig[taxIDNumberTemplate(property)] || '',
        [taxIDTypeTemplate(shipperType, property)]:
          labelConfig[taxIDTypeTemplate(shipperType, property)] || '',
        [taxIssuerCountryTemplate(property)]:
          labelConfig[taxIssuerCountryTemplate(property)] || '',
      }),
      {},
    ),
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  ({properties, shipperType, ...form}) => {
    const errors = {}

    for (const property of properties) {
      const idLabelProperty = taxIDNumberTemplate(property)
      const typeLabelProperty = taxIDTypeTemplate(shipperType, property)
      const countryLabelProperty = taxIssuerCountryTemplate(property)
      const taxID = form[idLabelProperty]
      const taxType = form[typeLabelProperty]
      const taxCountry = form[countryLabelProperty]

      if (isPresent(taxID) && !isPresent(taxType)) {
        errors[typeLabelProperty] = 'Tax ID Type is required'
        errors.preventSave = true
      }

      if (
        TAX_INFO_NEEDS_COUNTRY_SHIPPERS.includes(shipperType) &&
        isPresent(taxID) &&
        !isPresent(taxCountry)
      ) {
        errors[countryLabelProperty] = 'Tax Issuer Country is required'
        errors.preventSave = true
      }
    }

    return errors
  },
)

function updatedParamsSelector(state) {
  const {properties, labelInfoID, shipperType, ...form} =
    modalFormSelector(state)
  const labelConfig = labelConfigSelector(state, {
    labelInfoID,
  })

  const updates = {}

  for (const property of properties) {
    const idLabelProperty = taxIDNumberTemplate(property)
    const typeLabelProperty = taxIDTypeTemplate(shipperType, property)
    const countryLabelProperty = taxIssuerCountryTemplate(property)
    const taxID = form[idLabelProperty]
    const taxType = form[typeLabelProperty]
    const taxCountry = form[countryLabelProperty]

    if (labelConfig[idLabelProperty] !== taxID) {
      updates[idLabelProperty] = taxID
    }

    if (labelConfig[typeLabelProperty] !== taxType) {
      updates[typeLabelProperty] = taxType
    }

    if (
      TAX_INFO_NEEDS_COUNTRY_SHIPPERS.includes(shipperType) &&
      labelConfig[countryLabelProperty] !== taxCountry
    ) {
      updates[countryLabelProperty] = taxCountry
    }
  }

  return updates
}

export async function updateTaxInfo() {
  try {
    const form = modalFormSelector(getState())
    const params = updatedParamsSelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    if (Object.keys(params).length) {
      updateLabelConfig(form.labelInfoID, params)

      await getRates([form.labelInfoID], {justSaveLabelConfig: true})

      showMessageToast('Update Tax Info')
    }

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function TaxInfoForm({property, autoFocus}) {
  const form = useSelector(modalFormSelector)
  const errors = useSelector(errorsSelector)
  const idLabelProperty = taxIDNumberTemplate(property)
  const typeLabelProperty = taxIDTypeTemplate(form.shipperType, property)
  const countryLabelProperty = taxIssuerCountryTemplate(property)
  const taxID = form[idLabelProperty]
  const taxType = form[typeLabelProperty]
  const taxCountry = form[countryLabelProperty]

  const options = ShipperOptions[form.shipperType].tax_id_type || []
  const needsCountryInput = TAX_INFO_NEEDS_COUNTRY_SHIPPERS.includes(
    form.shipperType,
  )
  const prefix = property === SENDER_TAX_INFO ? 'Sender' : 'Receiver'

  return (
    <>
      <li className="list__item list__item--form">
        <div className="w-50">
          <TextInput
            label={`${prefix} Tax ID`}
            id={idLabelProperty}
            className="margin-bottom-0"
            value={taxID}
            onChange={(value) => updateModalForm({[idLabelProperty]: value})}
            errorMessage={errors[idLabelProperty]}
            autoFocus={autoFocus}
          />
        </div>
      </li>
      <li className="list__item list__item--form">
        <Select
          label={`${prefix} Tax ID Type`}
          id={typeLabelProperty}
          value={taxType}
          onChange={(value) => updateModalForm({[typeLabelProperty]: value})}
          errorMessage={errors[typeLabelProperty]}
        >
          <option value="" />
          {options.map(({value, display}) => (
            <option key={value} value={value}>
              {display}
            </option>
          ))}
        </Select>
      </li>
      {needsCountryInput && (
        <li className="list__item list__item--form">
          <div className="w-50">
            <TextInput
              label={`${prefix} Tax Issuer Country`}
              id={countryLabelProperty}
              className="margin-bottom-0"
              value={taxCountry}
              onChange={(value) =>
                updateModalForm({[countryLabelProperty]: value})
              }
              errorMessage={errors[countryLabelProperty]}
            />
          </div>
        </li>
      )}
    </>
  )
}

function TaxInfoModal({form}) {
  const errors = useSelector(errorsSelector)

  return (
    <ConfirmModal
      title="Edit Tax Info"
      modalSize="sm"
      onConfirm={() => updateTaxInfo()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
    >
      <ul className="list list--no-style">
        {form.properties.map((property, index) => (
          <TaxInfoForm
            key={property}
            property={property}
            autoFocus={index === 0}
          />
        ))}
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(TaxInfoModal, modalFormSelector)
